import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { brandInfo, footerLogo, navLinks, productMenu, whatsappUrl } from '@/mocks/homeData';

const HEADER_OFFSET = 96;

function scrollToId(id: string, attempt = 0) {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    return;
  }
  if (attempt < 12) {
    window.setTimeout(() => scrollToId(id, attempt + 1), 100);
  }
}

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.replace('#', ''));
      const timer = window.setTimeout(() => scrollToId(id), 60);
      return () => window.clearTimeout(timer);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
    return undefined;
  }, [location.pathname, location.hash]);

  const closeAll = () => {
    setMobileOpen(false);
    setProductsOpen(false);
  };

  const handleNavClick = (to: string) => {
    closeAll();
    const [path, hash] = to.split('#');
    if (hash && location.pathname === (path || '/')) {
      scrollToId(hash);
    }
  };

  const isActive = (to: string) => {
    if (to.includes('#')) {
      const [path, hash] = to.split('#');
      return location.pathname === (path || '/') && location.hash === `#${hash}`;
    }
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-background-50'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between gap-4 h-[68px] md:h-[76px]">
          {/* Logo */}
          <Link to="/" onClick={closeAll} className="flex items-center gap-3 cursor-pointer shrink-0">
            {!logoError ? (
              <img
                src={footerLogo}
                alt="Best Research Pharma logo"
                title="Best Research Pharma - Ayurvedic Medicines"
                onError={() => setLogoError(true)}
                className="h-10 md:h-12 w-auto object-contain"
              />
            ) : (
              <span className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-leaf-fill text-xl"></i>
              </span>
            )}
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-heading text-base md:text-lg font-semibold text-primary-800 tracking-tight">
                Best Research Pharma
              </span>
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-secondary-700 mt-0.5">
                {brandInfo.tagline}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.id === 'products' ? (
                <div
                  key={link.id}
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <Link
                    to={link.to}
                    onClick={closeAll}
                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      isActive(link.to)
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-foreground-700 hover:text-primary-700 hover:bg-primary-50'
                    }`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${productsOpen ? 'rotate-180' : ''}`}></i>
                  </Link>
                  <div
                    className={`absolute left-0 top-full pt-2 w-56 transition-all duration-200 ${
                      productsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
                    }`}
                  >
                    <div className="rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg shadow-primary-900/5">
                      {productMenu.map((item) => (
                        <Link
                          key={item.id}
                          to={item.to}
                          onClick={closeAll}
                          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-secondary-50 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                            <i className={`${item.icon} text-sm`}></i>
                          </span>
                          {item.label}
                        </Link>
                      ))}
                      <Link
                        to="/products"
                        onClick={closeAll}
                        className="mt-1 flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        View All Products
                        <i className="ri-arrow-right-line text-base"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.id}
                  to={link.to}
                  onClick={() => handleNavClick(link.to)}
                  className={`relative px-3 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    isActive(link.to)
                      ? 'text-primary-700 bg-primary-50'
                      : 'text-foreground-700 hover:text-primary-700 hover:bg-primary-50'
                  }`}
                >
                  {link.label}
                  {isActive(link.to) ? (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-primary-500"></span>
                  ) : null}
                </Link>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAll}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              Enquire Now
            </a>

            <button
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      <div
        className={`lg:hidden transition-all duration-300 border-t border-background-200 bg-background-50 ${
          mobileOpen ? 'max-h-[780px] overflow-y-auto' : 'max-h-0 overflow-hidden'
        }`}
      >
        <nav className="px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              to={link.to}
              onClick={() => handleNavClick(link.to)}
              className={`relative px-3 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer ${
                isActive(link.to)
                  ? 'text-primary-700 bg-primary-50'
                  : 'text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
              }`}
            >
              {link.label}
              {isActive(link.to) ? (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-primary-500"></span>
              ) : null}
            </Link>
          ))}
          <div className="mt-2 pt-3 border-t border-background-200">
            <p className="px-3 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-700 mb-2">
              Product Range
            </p>
            <div className="grid grid-cols-2 gap-2">
              {productMenu.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={closeAll}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary-50 text-sm text-foreground-700 cursor-pointer"
                >
                  <i className={`${item.icon} text-secondary-600`}></i>
                  {item.label}
                </Link>
              ))}
            </div>
            <Link
              to="/products"
              onClick={closeAll}
              className="mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-primary-300 text-primary-700 text-sm font-semibold cursor-pointer"
            >
              View All Products
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeAll}
            className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Enquire Now
          </a>
        </nav>
      </div>
    </header>
  );
}